import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ORingService } from 'src/app/services/oring.service';

import { Store } from '@ngrx/store';
import { catchError, map, merge, of, switchMap, withLatestFrom } from 'rxjs';
import * as fromORing from '../fromORing.barrel';
import * as fromStore from '../index';

@Injectable()
export class ORingEffects {
  constructor(
    private actions$: Actions,
    private ORingService: ORingService,
    private store: Store<fromORing.State>
  ) { }

  loadORings$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadORings),
    withLatestFrom(this.store.select(fromORing.selectActiveFilter)),
    switchMap(([action, fil]) => this.ORingService.get({})
      .pipe(map(data => fromORing.loadORingsSuccess({ data })),
        catchError(error => of(fromORing.loadORingsFail({ error })))))
  ));

  /// causes the app to load the results when the filter changes
  onUpdateORingFilter$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.updateORingFilter, fromORing.toggleSellableCode),
    map(action => fromORing.loadORings())
  ));

  clearActiveOnSearchResult$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.SEARCH.loadSearchResultsSuccess),
    map(action => fromORing.clearSelectedProduct())
  ));
  
  /// causes the app to load the results when the selected product changes
  onSelectProduct$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.toggleSelectedProduct),
    withLatestFrom(this.store.select(fromORing.selectSelectedProduct)),
    map(([action, product]) => {
      if (!product) {
        return fromORing.loadORings();
      }
      return fromORing.loadSelectedProductSuccess();
    })
  ));

  /// Add all the lookups that you want to dispatch at once here... ↓↓↓↓
  loadLookups$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadORingsLookups),
    switchMap(action => merge(
      of(fromORing.loadPipeSizes()),
      of(fromORing.loadMaterials()),
      of(fromORing.loadProductFamilies()),
      of(fromORing.loadSBUs()),
      of(fromORing.loadSubSBUs()),
      of(fromORing.loadSellables()),
      of(fromORing.loadProductTypes())
    ))));

  loadPipeSizes$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadPipeSizes),
    switchMap(action => this.ORingService.getPipeSizes()
      .pipe(map(data => fromORing.loadPipeSizesSuccess({ data })),
        catchError(error => of(fromORing.loadPipeSizesFail({ error })))))
  ));

  loadMaterials$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadMaterials),
    switchMap(action => this.ORingService.getMaterials()
      .pipe(
        map(res => fromORing.loadMaterialsSuccess({ materials: res })),
        catchError(error => of(fromORing.loadMaterialsFail({ error })))))
  ));

  loadProductFamilies$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadProductFamilies),
    switchMap(action => this.ORingService.getProductFamilies()
      .pipe(map(res => fromORing.loadProductFamiliesSuccess({ families: res })),
        catchError(error => of(fromORing.loadProductFamiliesFail({ error })))))
  ));

  loadSbus$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadSBUs),
    switchMap(action => this.ORingService.getSbus()
      .pipe(map(res => fromORing.loadSBUsSuccess({ sbus: res })),
        catchError(error => of(fromORing.loadSubSBUsFail({ error })))))
  ));

  loadSubSbus$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadSubSBUs),
    switchMap(action => this.ORingService.getSubSBUs()
      .pipe(map(res => fromORing.loadSubSBUsSuccess({ subSBUs: res })),
        catchError(error => of(fromORing.loadSubSBUsFail({ error })))))
  ));

  loadSellables$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadSellables),
    switchMap(action => this.ORingService.getSellables()
      .pipe(map(res => fromORing.loadSellablesSuccess({ sellables: res })),
        catchError(error => of(fromORing.loadSellablesFail({ error })))))
  ));

  loadEquipmentTypes$ = createEffect(() => this.actions$.pipe(
    ofType(fromORing.loadProductTypes),
    switchMap(action => this.ORingService.getProductTypes()
      .pipe(map(productTypes => fromORing.loadProductTypesSuccess({ productTypes })),
        catchError(error => of(fromORing.loadProductTypesFail({ error })))))));
}
